import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import {useTranslation} from 'react-i18next';
import {SUPPORTED_FISH_CERTIFICATES} from 'appConstants';
import {Checkbox, Chip, ListItemIcon, ListItemText, TextField} from '@mui/material';
import {type HarvestDemandPutCertificatesEnum} from 'client';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

type CertificatesSelectorProps = {
  certificates: HarvestDemandPutCertificatesEnum[];
  setCertificates: (certificates: HarvestDemandPutCertificatesEnum[]) => void;
} & React.ComponentProps<typeof TextField>;

export function CertificatesSelector({certificates, setCertificates, ...other}: CertificatesSelectorProps) {
  const {t} = useTranslation('common');

  return (
    <TextField
      select
      fullWidth
      label={t('certificates')}
      slotProps={{
        select: {
          multiple: true,
          renderValue: (value) => {
            const selected = value as HarvestDemandPutCertificatesEnum[];

            return (
              <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                {selected && selected.map((name) => <Chip key={name} label={t(name)} />)}
              </Box>
            );
          },
          MenuProps,
        },
      }}
      value={certificates}
      data-testid="certificates-control"
      {...other}
    >
      {SUPPORTED_FISH_CERTIFICATES.map((certificate) => (
        <CertificateMenuItem key={certificate} {...{certificate, certificates, setCertificates}} />
      ))}
    </TextField>
  );
}

const CertificateMenuItem = ({
  certificate,
  certificates,
  setCertificates,
}: {
  certificate: HarvestDemandPutCertificatesEnum;
  certificates: HarvestDemandPutCertificatesEnum[];
  setCertificates: (certificates: HarvestDemandPutCertificatesEnum[]) => void;
}) => {
  const {t} = useTranslation('common');
  return (
    <MenuItem
      onClick={() => {
        if (certificates && certificates?.includes(certificate)) {
          setCertificates(certificates.filter((i) => i !== certificate));
        } else {
          certificates && setCertificates([...certificates, certificate]);
        }
      }}
      data-testid={`certificates-${certificate}`}
    >
      <ListItemIcon>
        <Checkbox disableRipple checked={certificates?.includes(certificate)} sx={{p: 0}} />
      </ListItemIcon>
      <ListItemText>{t(certificate)}</ListItemText>
    </MenuItem>
  );
};
